import { VolumeIllustrationModel } from "api"
import { useIsDesktop } from "common/MediaQueries"
import Image from "next/image"
import { useEffect, useMemo, useState } from "react"
import MultibleChipsRow from "reusable/chips/MultibleChipsRow"
import styled from "styled-components"
import {
  IconBaby,
  IconBag,
  IconBooster,
  IconCarSide,
  IconCarTop,
  IconChild,
  IconSit,
  IconSuitcase,
  IconTrolly,
} from "./IllustrateIcons"

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 600px;
  ${(p) => p.theme.breakpoints.up("md")} {
    align-items: center;
  }
  h2 {
    font-weight: normal;
    width: 100%;
    text-align: center;
    ${(p) => p.theme.breakpoints.down("sm")} {
      padding: 26px 8px 32px;
      text-align: start;
      margin: 0;
    }
  }
  img {
    object-fit: cover;
  }
  .buttons-wrapper {
    margin: 16px 20px;
    ${(p) => p.theme.breakpoints.down("sm")} {
      margin: 30px 8px 16px;
    }
    .icon-buttons > div {
      gap: 16px;
      ${(p) => p.theme.breakpoints.down("sm")} {
        justify-content: start;
      }
      > button > label > div {
        padding: 10px 14px;
        &:not(.icon) {
          background-color: ${(p) => p.theme.colors.white};

          p {
            color: ${(p) => p.theme.colors.black};
          }
        }
      }
    }
  }
`

type ImageWithButtonsType = {
  item: VolumeIllustrationModel
  index: number
}

const icons1 = [IconCarTop, IconCarSide]
const icons3 = [IconBaby, IconBag, IconTrolly, IconSuitcase]
const icons2 = [IconChild, IconSit, IconBooster]
const icons = [icons1, icons2, icons3]

const ImageWithButtons = ({ item, index }: ImageWithButtonsType) => {
  const isDesktop = useIsDesktop()
  const defaultImage = item?.defaultImage
  const allImage = item?.allImage
  const [btnText, setBtnText] = useState("בחר הכול")
  const [currImage, setCurrImage] = useState(defaultImage)
  const [showAll, setShowAll] = useState(false)
  const iconsIndexList = useMemo(() => {
    const indexes = item?.imageList.map(() => `icon`)
    if (index !== 0) return [...indexes, btnText]
    return indexes
  }, [item?.imageList, btnText])
  const [currIndex, setCurrIndex] = useState(item?.imageList.length)
  useEffect(() => {
    if (index === 0) setCurrIndex(0)
    return () => {
      setCurrIndex(item?.imageList?.length)
    }
  }, [])

  useEffect(() => {
    switch (true) {
      case item?.imageList[currIndex] !== undefined: // default icon behavior
        setCurrImage(item?.imageList[currIndex])
        setBtnText("בחר הכול")
        setShowAll(true)
        break
      case item?.imageList[0] !== undefined && defaultImage === null: // default icon behavior in cases where there is no default image
        setCurrIndex(0)
        break
      case currIndex === item?.imageList.length && showAll: // activate show all mode
        setCurrIndex(item?.imageList?.length + 1)
        setCurrImage(allImage)
        setBtnText("נקה הכול")
        setShowAll(false)
        break
      case currIndex === item?.imageList.length && !showAll: // deactivate show all mode and initial state
        setCurrIndex(item?.imageList?.length + 1)
        setCurrImage(defaultImage)
        setBtnText("בחר הכול")
        setShowAll(true)
        break
    }
  }, [currIndex])

  return (
    <StyledWrapper>
      <h2>{item?.title}</h2>
      {currImage?.imageUrl && (
        <Image
          src={currImage?.imageUrl}
          width={isDesktop ? 513 : 360}
          height={isDesktop ? 402 : 270}
          alt={currImage?.alt || ""}
          style={{
            maxWidth: "100%",
            height: "auto",
            objectFit: "contain",
          }}
        />
      )}
      <div className="buttons-wrapper">
        <MultibleChipsRow
          activeButton={currIndex}
          setActiveButton={setCurrIndex}
          isSelectAll={!showAll}
          optionsList={iconsIndexList}
          icons={icons[index]}
          toggleId="tab-selection"
          className="icon-buttons"
        />
      </div>
    </StyledWrapper>
  )
}

export default ImageWithButtons
